import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// ── utilidades de texto ─────────────────────────────────────────────────────

const DIACRITICOS = /[̀-ͯ]/g;

export const normalizar = (texto) =>
    (texto || '').normalize('NFD').replace(DIACRITICOS, '').toLowerCase();

export const urlArquivo = (relativa) => `${process.env.PUBLIC_URL}/${relativa}`;

// ── indexação ───────────────────────────────────────────────────────────────

function contarDocumentos(pasta) {
    return pasta.documentos.length + pasta.subpastas.reduce((s, p) => s + contarDocumentos(p), 0);
}

export function indexar(dados) {
    const pastasPorId = {};
    const documentos = [];
    const pastas = [];

    const percorrer = (pasta, caminho) => {
        const atual = [...caminho, { id: pasta.id, titulo: pasta.titulo }];
        pastasPorId[pasta.id] = { ...pasta, caminho: atual, total: contarDocumentos(pasta) };
        pastas.push(pastasPorId[pasta.id]);

        pasta.documentos.forEach((doc) => {
            documentos.push({
                ...doc,
                caminho: atual,
                _busca: {
                    titulo: normalizar(doc.titulo),
                    descricao: normalizar(doc.descricao),
                    arquivo: normalizar(`${doc.arquivo} ${doc.tipo}`),
                    caminho: normalizar(atual.map((p) => p.titulo).join(' ')),
                },
            });
        });
        pasta.subpastas.forEach((sub) => percorrer(sub, atual));
    };
    dados.pastas.forEach((p) => percorrer(p, []));

    return {
        raiz: dados.pastas.map((p) => pastasPorId[p.id]),
        links: dados.links || [],
        geradoEm: dados.geradoEm,
        pastasPorId,
        pastas,
        documentos,
    };
}

// ── busca ───────────────────────────────────────────────────────────────────

export const termosDaConsulta = (consulta) =>
    normalizar(consulta).split(/\s+/).filter(Boolean);

export function buscar(indice, consulta) {
    const termos = termosDaConsulta(consulta);
    if (!termos.length) return { termos, pastas: [], documentos: [] };

    const pastas = indice.pastas.filter((p) => {
        const texto = normalizar(`${p.titulo} ${p.descricao}`);
        return termos.every((t) => texto.includes(t));
    });

    const documentos = indice.documentos
        .map((doc) => {
            const b = doc._busca;
            let pontos = 0;
            for (const t of termos) {
                let achou = false;
                if (b.titulo.includes(t)) { pontos += 3; achou = true; }
                if (b.descricao.includes(t)) { pontos += 2; achou = true; }
                if (b.caminho.includes(t)) { pontos += 1; achou = true; }
                if (b.arquivo.includes(t)) { pontos += 1; achou = true; }
                if (!achou) return null; // todos os termos precisam aparecer
            }
            return { doc, pontos };
        })
        .filter(Boolean)
        .sort((a, b) => b.pontos - a.pontos)
        .map((r) => r.doc);

    return { termos, pastas, documentos };
}

// ── contexto ────────────────────────────────────────────────────────────────

const CatalogoContext = createContext(null);

export function CatalogoProvider({ children }) {
    const [estado, setEstado] = useState({ carregando: true, erro: null, dados: null });

    useEffect(() => {
        let ativo = true;
        fetch(`${process.env.PUBLIC_URL}/dados/documentos.json`)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((dados) => ativo && setEstado({ carregando: false, erro: null, dados }))
            .catch((erro) => ativo && setEstado({ carregando: false, erro, dados: null }));
        return () => { ativo = false; };
    }, []);

    const indice = useMemo(() => (estado.dados ? indexar(estado.dados) : null), [estado.dados]);

    const valor = useMemo(
        () => ({ carregando: estado.carregando, erro: estado.erro, indice }),
        [estado.carregando, estado.erro, indice]
    );

    return <CatalogoContext.Provider value={valor}>{children}</CatalogoContext.Provider>;
}

export const useCatalogo = () => useContext(CatalogoContext);
